<template>
  <!-- Desktop Row -->
  <tr class="quantity-card" v-if="!isMobile">
    <td class="pt-4 pb-3" style="vertical-align: top">
      <span class="text-h6">{{ passType.name }}</span>
      <div
        v-if="isMultiDay && coveredRange"
        class="text-green-darken-2 font-weight-bold text-body-2 mt-1"
      >
        Covers {{ coveredRange }} &middot; {{ datesPerPass }} days
      </div>
      <div class="mt-1">
        <v-chip
          v-for="d in sortedDates"
          :key="d.id ?? 0"
          size="x-small"
          variant="tonal"
          class="mr-1 mb-1"
        >
          {{ formatDate(d.eventStartDateTime) }}
        </v-chip>
      </div>
      <div class="text-caption text-grey-darken-3 pt-1">
        {{ formatCurrency(unitPrice) }} +
        <span class="text-no-wrap">
          {{ formatCurrency(unitServiceFee) }} service fee
        </span>
        <div class="font-weight-bold" v-if="isMultiDay">
          {{ formatCurrency(perDayPrice) }} per day &times; {{ datesPerPass }}
          dates
        </div>
        <div class="font-weight-bold" v-if="perPassAfterFees">
          {{ formatCurrency(perPassAfterFees) }} per pass after taxes and fees
        </div>
      </div>
      <div class="text-caption text-grey-darken-3 pt-1">
        <span v-if="passType.maxQuantityPerPurchase">
          Max Purchase Qty: {{ passType.maxQuantityPerPurchase }}
        </span>
      </div>
    </td>
    <td class="text-center py-4">
      <div class="d-flex align-center justify-center flex-column">
        <quantity-input
          :value="localQuantity"
          @update="handleQuantityUpdate($event)"
          :max="maxQuantity"
          class="mr-2"
          :loading="isUpdatingQuantity"
          :disable-plus="anyDateSoldOut"
          :aria-label="`${passType.name} quantity`"
        />
        <div
          v-if="anyDateSoldOut"
          class="text-error font-weight-bold mt-1 sold-out-desktop"
        >
          Sold Out
        </div>
        <div
          v-else-if="showRemaining"
          class="text-caption text-medium-emphasis mt-1"
        >
          {{ inventoryRemaining }} remaining
        </div>
        <div
          v-if="quantityError"
          class="text-error text-caption mt-1"
          role="alert"
        >
          {{ quantityError }}
        </div>
      </div>
    </td>
    <td></td>
    <td class="ticket-price-total-cell text-right">
      <v-fade-transition mode="out-in">
        <template v-if="isUpdatingQuantity">
          <v-progress-circular indeterminate size="20" color="primary" />
        </template>
        <div v-else>{{ formatCurrency(priceSubtotal) }}</div>
      </v-fade-transition>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>{{ formatCurrency(serviceFee) }}</div>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>{{ formatCurrency(convenienceFee) }}</div>
    </td>
    <td class="text-right">
      <template v-if="isUpdatingQuantity">
        <v-progress-circular indeterminate size="20" color="primary" />
      </template>
      <div v-else>{{ formatCurrency(salesTax) }}</div>
    </td>
    <td class="ticket-price-total-cell text-right">
      <v-fade-transition mode="out-in">
        <template v-if="isUpdatingQuantity">
          <v-progress-circular indeterminate size="20" color="primary" />
        </template>
        <div v-else>{{ formatCurrency(totalPrice) }}</div>
      </v-fade-transition>
    </td>
  </tr>

  <!-- Mobile Row -->
  <v-card class="quantity-card" v-else>
    <div class="text-h6">{{ passType.name }}</div>
    <div
      v-if="isMultiDay && coveredRange"
      class="text-green-darken-2 font-weight-bold text-body-2 mt-1"
    >
      Covers {{ coveredRange }} &middot; {{ datesPerPass }} days
    </div>
    <div class="mt-1">
      <v-chip
        v-for="d in sortedDates"
        :key="d.id ?? 0"
        size="x-small"
        variant="tonal"
        class="mr-1 mb-1"
      >
        {{ formatDate(d.eventStartDateTime) }}
      </v-chip>
    </div>
    <div class="text-caption text-grey-darken-3">
      {{ formatCurrency(unitPrice) }} +
      <span class="text-no-wrap">
        {{ formatCurrency(unitServiceFee) }} service fee
      </span>
      <div class="font-weight-bold" v-if="perPassAfterFees">
        {{ formatCurrency(perPassAfterFees) }} per pass after taxes and fees
      </div>
      <div class="text-caption text-wrap text-right">
        <ul>
          <li>Service Fee: {{ formatCurrency(serviceFee) }}</li>
          <li>Convenience Fee: {{ formatCurrency(convenienceFee) }}</li>
          <li>Sales Tax: {{ formatCurrency(salesTax) }}</li>
        </ul>
      </div>
    </div>
    <div class="text-caption pt-1">
      <span v-if="passType.maxQuantityPerPurchase">
        Max Purchase Qty: {{ passType.maxQuantityPerPurchase }}
      </span>
    </div>
    <div class="quantity-row">
      <div class="d-flex align-center flex-column">
        <quantity-input
          :value="localQuantity"
          @update="handleQuantityUpdate($event)"
          :max="maxQuantity"
          size="small"
          :loading="isUpdatingQuantity"
          :disable-plus="anyDateSoldOut"
          :aria-label="`${passType.name} quantity`"
        />
        <div
          v-if="anyDateSoldOut"
          class="text-caption text-error font-weight-bold mt-1 sold-out-mobile"
        >
          Sold Out
        </div>
        <div
          v-else-if="showRemaining"
          class="text-caption text-medium-emphasis mt-1"
        >
          {{ inventoryRemaining }} remaining
        </div>
        <div
          v-if="quantityError"
          class="text-caption text-error mt-1"
          role="alert"
        >
          {{ quantityError }}
        </div>
      </div>
      <v-fade-transition mode="out-in">
        <v-skeleton-loader v-if="isUpdatingQuantity" type="text" />
        <div v-else class="subtotal-value-mobile">
          {{ formatCurrency(totalPrice) }}
        </div>
      </v-fade-transition>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { PassType } from "@/models.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import { useDisplay } from "vuetify";
import { computed, ref, watch } from "vue";
import { formatCurrency } from "@common/utils";
import { formatPassDateRange } from "@common/purchase/series-cart";
import {
  sortedPassDates,
  datesPerPass as datesPerPassOf,
  currentQuantity as currentQuantityOf,
  anyDateSoldOut as anyDateSoldOutOf,
  passInventoryRemaining as passInventoryRemainingOf,
  maxQuantity as maxQuantityOf,
  effectiveUnitPrice,
  effectiveUnitServiceFee,
  effectivePerDayPrice,
  isMultiDayPass,
  sumPassTickets,
  quantityUpdateError,
} from "@common/purchase/pass-quantity-selector-logic";

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);

const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  passType: PassType;
  // Injected by the parent selector so a row works against either the public or the private
  // (agent) service. Concurrency + reload handling are configured once on the parent, not here.
  setQuantityCaller: PublicPurchaseServiceViewModel["setSeriesPassTypeQuantity"];
}>();

const localQuantity = ref(0);
const isUpdatingQuantity = ref(false);
const quantityError = ref<string | null>(null);
const QUANTITY_UPDATE_FAILED = "Couldn't update quantity. Please try again.";

const tickets = computed(() => props.purchase.tickets ?? []);

const sortedDates = computed(() => sortedPassDates(props.passType));
// Reuse the cart's date-range formatter so the "Covers …" line matches the cart summary exactly
// (one tested formatter, no dash-style drift between the two places that show a pass's dates).
const coveredRange = computed(() =>
  formatPassDateRange(props.passType.passTypeDates ?? []),
);
const datesPerPass = computed(() => datesPerPassOf(props.passType));
const unitPrice = computed(() =>
  effectiveUnitPrice(props.passType, tickets.value),
);
const unitServiceFee = computed(() =>
  effectiveUnitServiceFee(props.passType, tickets.value),
);
const perDayPrice = computed(() =>
  effectivePerDayPrice(props.passType, tickets.value),
);
const isMultiDay = computed(() => isMultiDayPass(props.passType));
const currentQuantity = computed(() =>
  currentQuantityOf(props.passType, tickets.value),
);
const maxQuantity = computed(() => maxQuantityOf(props.passType));
const anyDateSoldOut = computed(() => anyDateSoldOutOf(props.passType));
// Remaining inventory across the pass's dates. `+` is already blocked at zero via anyDateSoldOut
// (per-price remaining counts in-cart tickets), so this only drives the low-inventory hint.
const inventoryRemaining = computed(() =>
  passInventoryRemainingOf(props.passType),
);
// Only surface the count when inventory is low, gated by the pass price's threshold exactly like
// normal events (purchase-quantity-selector-row's showLowInventoryAlert) — no "500 remaining" noise.
const lowInventoryThreshold = computed(
  () => props.passType.eventPrice?.lowInventoryThreshold ?? 0,
);
const showRemaining = computed(
  () =>
    !anyDateSoldOut.value &&
    lowInventoryThreshold.value > 0 &&
    inventoryRemaining.value !== null &&
    inventoryRemaining.value > 0 &&
    inventoryRemaining.value <= lowInventoryThreshold.value,
);

const priceSubtotal = computed(() =>
  sumPassTickets(props.passType.id, tickets.value, (t) => t.price),
);
const serviceFee = computed(() =>
  sumPassTickets(props.passType.id, tickets.value, (t) => t.fees),
);
const convenienceFee = computed(() =>
  sumPassTickets(props.passType.id, tickets.value, (t) => t.convenienceFee),
);
const salesTax = computed(() =>
  sumPassTickets(props.passType.id, tickets.value, (t) => t.salesTax),
);
const totalPrice = computed(() =>
  sumPassTickets(props.passType.id, tickets.value, (t) => t.totalValue),
);
const perPassAfterFees = computed(() =>
  currentQuantity.value > 0 ? totalPrice.value / currentQuantity.value : 0,
);

watch(currentQuantity, (q) => (localQuantity.value = q), { immediate: true });

async function handleQuantityUpdate(val: number) {
  if (val === currentQuantity.value || isUpdatingQuantity.value) return;
  localQuantity.value = val;
  isUpdatingQuantity.value = true;
  quantityError.value = null;
  const caller = props.setQuantityCaller;
  try {
    await caller(props.purchase.publicId, props.passType.id, val);
  } catch (error) {
    // A failed ItemResult (capacity, max-per-order, sold out) rejects as an HTTP error but still
    // populates the caller state we read below; log the raw error only for diagnostics.
    console.error("Failed to update pass quantity:", error);
  }
  // Show why it failed and roll the quantity back so the cart and the input stay in sync.
  quantityError.value = quantityUpdateError(caller, QUANTITY_UPDATE_FAILED);
  if (quantityError.value !== null) {
    localQuantity.value = currentQuantity.value;
  }
  isUpdatingQuantity.value = false;
}

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  return new Date(d as any).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
</script>
